import { IAnnouncerPack } from "./IAnnouncerPack";
import { AudioPlaybackHandle, IAudioManager } from "../../types/audio-manager";
import { MessageAnimationContext, MessageAnimationHandler } from "../../../../client/components/MultiPlayer/messageAnimations";

/**
 * Base class for announcer packs
 * Provides default implementations and shared functionality
 */
export abstract class BaseAnnouncerPack implements IAnnouncerPack {
  protected audioManager?: IAudioManager;
  private audioNameMap: Map<string, string> = new Map();
  private static readonly SUPPORTED_EVENT_TYPES = [
    "frag",
    "roundWin",
    "roundLose",
    "firstBlood",
    "multiKill",
    "killStreak",
  ];

  initialize(audioManager: IAudioManager): void {
    if (this.audioManager === audioManager && this.audioNameMap.size > 0) {
      return;
    }
    this.audioManager = audioManager;
    this.preloadAudio();
  }

  protected preloadAudio(): void {
    if (!this.audioManager) return;

    this.audioNameMap.clear();
    const packClassName = this.constructor.name;

    BaseAnnouncerPack.SUPPORTED_EVENT_TYPES.forEach(eventType => {
      const audioPath = this.getAudio(eventType);
      if (!audioPath) return;
      const audioName = `announcer_${packClassName}_${eventType}`;
      this.audioNameMap.set(eventType, audioName);
      this.audioManager!.loadSound(audioName, audioPath);
    });
  }

  /**
   * Create callback function for animation to play audio
   * Returns a thunk that, when executed, starts playback and provides cleanup.
   */
  protected createPlayAudioCallback(
    eventType: string
  ): (() => AudioPlaybackHandle | null) | null {
    if (!this.audioManager) return null;
    const audioName = this.audioNameMap.get(eventType);
    if (!audioName) return null;

    return () => this.audioManager!.playSound(audioName);
  }

  protected getAudioName(eventType: string): string | null {
    return this.audioNameMap.get(eventType) ?? null;
  }

  /**
   * Gets the animation handler for a specific event type
   * Override in subclasses to provide custom handlers
   */
  abstract getAnimationHandler(
    eventType: string,
    context: MessageAnimationContext
  ): MessageAnimationHandler | null;

  /**
   * Gets audio resources for events
   * Override in subclasses to provide custom audio
   */
  getAudio(eventType: string): string | null {
    return null; // Default: no audio
  }

  /**
   * Gets all audio file paths used by this pack
   * Default implementation collects all audio paths from getAudio() calls
   */
  getAllAudioPaths(): string[] {
    const paths: string[] = [];
    BaseAnnouncerPack.SUPPORTED_EVENT_TYPES.forEach(eventType => {
      const audioPath = this.getAudio(eventType);
      if (audioPath) {
        paths.push(audioPath);
      }
    });
    return paths;
  }
}

